import * as Sentry from '@sentry/react';
import { AppType, ApiResponse } from '@/types';
import { platformService } from '@/services/platform';
import { coreApiClient } from './core';
import { userStorage } from '@/utils/userStorage';

export const authApi = {
    /**
     * Initialize user - only for Telegram Mini App
     * For web/native apps, authentication is handled via OAuth and tokens
     */
    async initUser(appType: AppType, referralCode?: string): Promise<ApiResponse<{ authenticated: boolean; is_new_user?: boolean; token?: string }> & { _apiError?: unknown }> {
        try {
            // Only proceed if running in Telegram Mini App
            if (!platformService.isTelegramMiniApp()) {
                return {
                    success: false,
                    error: 'This method is only available in Telegram Mini App'
                };
            }

            const tg = platformService.getTelegramWebApp();

            if (!tg?.initData) {
                return {
                    success: false,
                    error: 'Telegram init data not available'
                };
            }

            const params = new URLSearchParams();
            params.append('_auth', tg.initData);

            if (referralCode) {
                params.append('referral_code', referralCode);
            }

            const url = `/api/init/${appType}?${params.toString()}`;

            const response = await coreApiClient.axios.post(url);

            if (response.data.ok || response.status === 200) {
                // Save token if provided
                if (response.data.token) {
                    await coreApiClient.saveToken(response.data.token);
                }

                userStorage.setItem('isAuthenticated', 'true');

                return {
                    success: true,
                    data: {
                        authenticated: true,
                        is_new_user: response.data.is_new_user,
                        token: response.data.token
                    }
                };
            }

            return {
                success: false,
                error: 'Failed to initialize user'
            };
        } catch (error) {
            console.error('Init user error:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'auth', auth_context: 'telegram_init' },
            });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to initialize user',
                _apiError: error,
            };
        }
    },

    async checkAuth(): Promise<boolean> {
        try {
            const response = await coreApiClient.axios.get('/api/auth/check');
            return response.data.authenticated === true;
        } catch {
            await coreApiClient.clearToken();
            return false;
        }
    },

    async logout(): Promise<void> {
        try {
            await coreApiClient.axios.post('/api/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
            Sentry.captureException(error, {
                tags: { error_type: 'auth', auth_context: 'logout' },
            });
        } finally {
            await coreApiClient.clearToken();
        }
    },

    /**
     * Generate token from existing session (migration helper)
     */
    async migrateSessionToToken(): Promise<ApiResponse<{ token: string }>> {
        try {
            const response = await coreApiClient.axios.post<ApiResponse<{ token: string }>>('/api/auth/token');

            if (response.data.success && response.data.data?.token) {
                await coreApiClient.saveToken(response.data.data.token);
            }

            return response.data;
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Failed to migrate session'
            };
        }
    }
};
